/**
 * Te reo Māori used across the app — especially during Te Wiki o te Reo Māori.
 * Tohutō (macrons) are required: Māori, not Maori; Whangārei, not Whangarei.
 */

import { occurrencesForYear } from './calendar';
import { isCampaignTemplateLive } from './campaignTemplates';
import { nzHourOfDay, nzWallClock } from './time';
import { TE_WIKI_SWIM_KUPU } from './teWikiKupu';

export const TE_REO_WEEK_SLUG = 'te-wiki-o-te-reo-maori';

/** Core kupu we put on the wall, in notifications, and on the welcome banner. */
export const TE_WIKI_KUPU = [
  { word: 'kia ora', gloss: 'hello / be well' },
  { word: 'kia kaha', gloss: 'be strong' },
  { word: 'ka rawe', gloss: 'excellent' },
  { word: 'whānau', gloss: 'family' },
  { word: 'kaiako', gloss: 'coach' },
  { word: 'ākonga', gloss: 'learner' },
  { word: 'rōpū', gloss: 'squad' },
  { word: 'kupu', gloss: 'word' },
  ...TE_WIKI_SWIM_KUPU,
] as const;

export type NavKey =
  | 'home'
  | 'store'
  | 'meets'
  | 'sessions'
  | 'copilot'
  | 'me'
  | 'deck'
  | 'squads'
  | 'calendar'
  | 'members'
  | 'guardians'
  | 'import'
  | 'feedback'
  | 'programs'
  | 'coach'
  | 'dashboard'
  | 'people'
  | 'communications'
  | 'finance';

export const TE_REO_NAV: Record<NavKey, { en: string; mi: string }> = {
  home: { en: 'Home', mi: 'Kāinga' },
  store: { en: 'Store', mi: 'Toa' },
  meets: { en: 'Meets', mi: 'Whakataetae' },
  sessions: { en: 'Sessions', mi: 'Kaukau' },
  copilot: { en: 'Co-pilot', mi: 'Kaiāwhina' },
  me: { en: 'Me', mi: 'Ahau' },
  deck: { en: "Today's deck", mi: 'Te rā nei' },
  squads: { en: 'Squads', mi: 'Rōpū' },
  calendar: { en: 'Calendar', mi: 'Maramataka' },
  members: { en: 'Members', mi: 'Mema' },
  guardians: { en: 'Guardians', mi: 'Kaitiaki' },
  import: { en: 'Import', mi: 'Kawemai' },
  feedback: { en: 'Feedback', mi: 'Urupare' },
  programs: { en: 'Programs', mi: 'Hōtaka' },
  coach: { en: 'Coach', mi: 'Kaiako' },
  dashboard: { en: 'Dashboard', mi: 'Papa' },
  people: { en: 'People', mi: 'Tāngata' },
  communications: { en: 'Communications', mi: 'Whakapā' },
  finance: { en: 'Finance', mi: 'Pūtea' },
};

export function isTeReoWeek(
  today: string = nzWallClock().isoDate,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
  calendar?: { occursOn: string; endsOn?: string | null } | null,
): boolean {
  const year = parseInt(today.slice(0, 4), 10);
  const occ = calendar?.occursOn
    ? { occursOn: calendar.occursOn, endsOn: calendar.endsOn ?? null }
    : Number.isFinite(year)
      ? occurrencesForYear(year, 'northland').find((d) => d.slug === TE_REO_WEEK_SLUG)
      : undefined;
  return isCampaignTemplateLive(
    TE_REO_WEEK_SLUG,
    today,
    occ?.occursOn,
    occ?.endsOn,
    previewSlug,
    nzHour,
    'awareness_day',
  );
}

export function navLabel(key: NavKey, teWiki = isTeReoWeek()): string {
  const row = TE_REO_NAV[key];
  return teWiki ? row.mi : row.en;
}

export function navTitle(key: NavKey): string {
  const row = TE_REO_NAV[key];
  return `${row.mi} · ${row.en}`;
}

/**
 * Time-of-day greeting in te reo (tohutō required).
 * Ata mārie (early morning), Mōrena (later morning),
 * Ahiahi mārie (afternoon), Pō mārie (evening / night).
 */
export function welcomeGreeting(now: Date = new Date(), _teWiki = isTeReoWeek()): string {
  const h = nzHourOfDay(now);
  if (h >= 5 && h < 10) return 'Ata mārie';
  if (h >= 10 && h < 12) return 'Mōrena';
  if (h >= 12 && h < 17) return 'Ahiahi mārie';
  return 'Pō mārie';
}

/** “Ata mārie, David” — greeting plus preferred / first name when we have one. */
export function welcomePersonGreeting(
  name: string | null | undefined,
  now: Date = new Date(),
  teWiki = isTeReoWeek(),
): string {
  const greeting = welcomeGreeting(now, teWiki);
  const trimmed = name?.trim();
  return trimmed ? `${greeting}, ${trimmed}` : greeting;
}

export function welcomeLine(teWiki = isTeReoWeek()): string {
  if (teWiki) {
    return 'Nau mai ki Te Wiki o te Reo Māori — ake, ake, ake.';
  }
  return '';
}
